/**
 * BillingDashboard Page
 * Shows subscription status and management options for yacht owners
 */

import React, { useState, useEffect } from 'react';
import { CreditCard, Check, AlertCircle, ExternalLink } from 'lucide-react';
import CountdownTimer, { isFreePeriod } from '../../components/CountdownTimer';
import PlanSelector from '../../components/PlanSelector';
import { billingAPI } from '../../services/api';

function BillingDashboard() {
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchBillingStatus();
  }, []);

  const fetchBillingStatus = async () => {
    try {
      setLoading(true);
      const response = await billingAPI.getStatus();
      setBillingStatus(response.data);
    } catch (err) {
      setError(err.message || 'Failed to load billing status');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await billingAPI.createPortalSession();
      if (response.data?.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (err) {
      setError(err.message || 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const inFreePeriod = isFreePeriod();
  const subscription = billingStatus?.subscription;
  const hasActiveSubscription = subscription?.status === 'active';
  const needsSubscription = !inFreePeriod && !hasActiveSubscription;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-navy-900">Billing & Subscription</h1>
        <p className="text-navy-600 mt-1">Manage your 1crew3 subscription</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Current Status Card */}
      <div className="bg-white rounded-xl border border-navy-200 p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${
            hasActiveSubscription || inFreePeriod ? 'bg-green-100' : 'bg-amber-100'
          }`}>
            <CreditCard className={`h-6 w-6 ${
              hasActiveSubscription || inFreePeriod ? 'text-green-600' : 'text-amber-600'
            }`} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-navy-900">Current Plan</h2>

            {inFreePeriod ? (
              <>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded text-sm font-medium">
                    <Check className="h-3 w-3" />
                    Free Trial Active
                  </span>
                </div>
                <p className="text-navy-600 text-sm mt-2">
                  You have full access to all features during the free period.
                </p>
              </>
            ) : hasActiveSubscription ? (
              <>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded text-sm font-medium">
                    <Check className="h-3 w-3" />
                    {subscription.plan === 'yearly' ? 'Yearly' : 'Monthly'} Plan
                  </span>
                </div>
                {subscription.current_period_end && (
                  <p className="text-navy-600 text-sm mt-2">
                    Next billing date: {new Date(subscription.current_period_end).toLocaleDateString()}
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-sm font-medium">
                    <AlertCircle className="h-3 w-3" />
                    {subscription?.status === 'canceled' ? 'Canceled' : 'Subscription Required'}
                  </span>
                </div>
                <p className="text-navy-600 text-sm mt-2">
                  Subscribe to continue using 1crew3 features.
                </p>
              </>
            )}
          </div>
        </div>

        {hasActiveSubscription && (
          <div className="mt-4 pt-4 border-t border-navy-100">
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm"
            >
              {portalLoading ? (
                <>
                  <span className="spinner h-4 w-4" />
                  Opening portal...
                </>
              ) : (
                <>
                  <ExternalLink className="h-4 w-4" />
                  Manage Subscription
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Free Period Countdown */}
      {inFreePeriod && (
        <div className="mb-6">
          <CountdownTimer />
        </div>
      )}

      {/* Plan Selection (show if needs subscription or in free period for preview) */}
      {(needsSubscription || inFreePeriod) && (
        <div className="bg-white rounded-xl border border-navy-200 p-6">
          <h2 className="text-lg font-semibold text-navy-900 mb-2">
            {inFreePeriod ? 'Subscribe Before Free Period Ends' : 'Choose Your Plan'}
          </h2>
          <p className="text-navy-600 text-sm mb-6">
            {inFreePeriod
              ? 'Lock in your subscription now and continue seamlessly after June 1st.'
              : 'Select a plan to regain access to all features.'}
          </p>
          <PlanSelector onError={(msg) => setError(msg)} />
        </div>
      )}

      {/* Features List */}
      <div className="mt-8 bg-navy-50 rounded-xl p-6">
        <h3 className="font-semibold text-navy-900 mb-4">What's Included</h3>
        <ul className="space-y-3">
          {[
            'Create unlimited mooring requests',
            'Connect with verified local helpers',
            'Real-time chat with helpers',
            'Rate and review helpers',
            'Manage your yacht profiles',
            'Track service history'
          ].map((feature, i) => (
            <li key={i} className="flex items-center gap-3 text-navy-700">
              <Check className="h-5 w-5 text-green-500" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default BillingDashboard;
